import React, { useState, useEffect } from "react";
import {
  Tag,
  Table,
  Button,
  Tooltip,
  Modal,
  Input,
  message,
  Typography,
  Space,
  Popconfirm,
  Upload,
  Divider,
} from "antd";
import { Helmet } from "react-helmet";
import axios from "axios";
import { useForm, Controller, set } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  CheckOutlined,
  EditOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import RequirePermission from "../components/Common/RequirePermission";

const { Title } = Typography;
const { Dragger } = Upload;

const schema = yup.object().shape({
  version_number: yup.string().required("Version is required"),
});

const MeetingRoomApk = () => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState([]);
  const [editPermission, setEditPermission] = useState(false);
  const [createPermission, setCreatePermission] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [s3Key, setS3Key] = useState("");
  const [fileInfo, setFileInfo] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleEdit = (record) => {
    reset(record);
    setEditId(record.id);
    setIsModalOpen(true);
  };

  const handleDefault = async (id) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/meeting_room_apk/default`,
        { id },
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count !== 0) {
        message.success("Apk set as live successfully");
        await fetchData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleUploadToS3 = async () => {
    try {
      setFileUploading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/meeting_room_apk/get-signed-url`,
        {
          params: {
            fileName: selectedFile.name,
            fileType: selectedFile.type,
          },
        },
      );

      const { signedUrl, fileKey, fileUrl } = response.data.data;
      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        body: selectedFile,
        headers: {
          "Content-Type": selectedFile.type,
        },
      });

      if (!uploadResponse.ok) throw new Error("Failed to upload to S3");

      setSelectedFile(null);
      setS3Key(fileUrl);
      setFileInfo({
        file_name: selectedFile.name,
        file_key: fileKey,
        file_path: fileUrl,
        mime_type: selectedFile.type,
      });
      message.success("File uploaded successfully");
    } catch (err) {
      console.log(err);
      message.error("Upload failed");
    } finally {
      setFileUploading(false);
    }
  };

  const handleDownloadApk = () => {
    const fileLink = details.filter((item) => item.id === editId)[0].apk_data;
    window.open(JSON.parse(fileLink).file_path, "_blank");
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + `/api/meeting_room_apk`,
      );
      const record = response.data.data;
      setDetails(record);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching app update data", error);
    }
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    const permissions = storedUser.permissions;
    setCreatePermission(permissions["signage.meeting_room_apk_create"] === "1");
    setEditPermission(permissions["signage.meeting_room_apk_edit"] === "1");
  }, []);

  useEffect(() => {
    const fetchTimer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(fetchTimer);
  }, []);

  const onUpdateForm = async (data) => {
    if (!s3Key) {
      message.error("Please upload file first");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/meeting_room_apk/update-apk`,
        {
          id: editId,
          version_number: data.version_number,
          apk_data: fileInfo,
        },
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count !== 0) {
        message.success("Apk updated successfully");
        await fetchData();
      }
      setSelectedFile(null);
      setS3Key("");
      setFileInfo(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onCreateForm = async (data) => {
    if (!s3Key) {
      message.error("Please upload file first");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/meeting_room_apk/add-apk`,
        {
          version_number: data.version_number,
          apk_data: fileInfo,
          status: "inactive",
        },
      );
      const inserted_id = response.data.data.inserted_id;
      if (inserted_id !== 0) {
        message.success("Apk added successfully");
        await fetchData();
      }
      setSelectedFile(null);
      setS3Key("");
      setFileInfo(null);
      setCreateModalOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Version Number",
      dataIndex: "version_number",
      key: "version_number",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "red"}>
          {status === "active" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Apk file path",
      dataIndex: "apk_data",
      key: "apk_data",
      render: (text, record) => (
        <span>{JSON.parse(record.apk_data).file_key}</span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          {editPermission && (
            <Tooltip title="Update Apk file">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {editPermission && (
            <Tooltip title="Set as live ">
              <Popconfirm
                title="Are you sure to set this apk as live?"
                onConfirm={() => handleDefault(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  icon={<CheckOutlined />}
                  size="small"
                  disabled={record.status === "active"}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];
  return (
    <RequirePermission permission="signage.meeting_room_view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Meeting Room Apk`}</title>
        </Helmet>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Title level={3} style={{ marginBottom: "1rem" }}>
            Meeting Room Apk
          </Title>
          <Button
            type="primary"
            onClick={() => setCreateModalOpen(true)}
            disabled={!createPermission}
          >
            Add Apk File
          </Button>
        </div>
        <Table
          loading={loading}
          columns={columns}
          dataSource={details}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          pagination={false}
        />
        {isModalOpen && (
          <Modal
            title="App Update"
            open={isModalOpen}
            onCancel={() => {
              reset();
              setIsModalOpen(false);
            }}
            footer={null}
            width={600}
          >
            <form onSubmit={handleSubmit(onUpdateForm)}>
              <div className="mb-3">
                <label className="form-label">
                  Version Number <span className="text-danger">*</span>
                </label>

                <Controller
                  name="version_number"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />

                {errors.version_number && (
                  <div className="text-danger small">
                    {errors.version_number.message}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                  <label className="form-label">
                    Apk File <span className="text-danger">*</span>
                  </label>
                  <Button type="link" size="small" onClick={handleDownloadApk}>
                    Download apk file
                  </Button>
                </div>

                <Dragger
                  maxCount={1}
                  beforeUpload={(file) => {
                    if (
                      file.type !== "application/vnd.android.package-archive"
                    ) {
                      message.error("Only .apk files are allowed");
                      return Upload.LIST_IGNORE;
                    }
                    setSelectedFile(file);
                    return false;
                  }}
                  onRemove={() => {
                    setSelectedFile(null);
                    setS3Key("");
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area
                  </p>
                  <p className="ant-upload-hint">Only .apk file is allowed</p>
                </Dragger>
              </div>
              <Button
                type="primary"
                loading={fileUploading}
                disabled={!selectedFile || s3Key}
                style={{
                  marginRight: "8px",
                  marginBottom: "8px",
                  width: "100%",
                }}
                onClick={handleUploadToS3}
              >
                Upload to S3
              </Button>

              {s3Key && (
                <div style={{ marginTop: "8px", color: "green" }}>
                  Uploaded to S3
                </div>
              )}
              <Divider plain>Update Apk</Divider>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={!s3Key || fileUploading}
                style={{
                  marginTop: "8px",
                  width: "100%",
                }}
              >
                Update Apk
              </Button>
            </form>
          </Modal>
        )}
        {createModalOpen && (
          <Modal
            title="Add Apk File"
            open={createModalOpen}
            onCancel={() => {
              reset();
              setCreateModalOpen(false);
            }}
            footer={null}
            width={600}
          >
            <form onSubmit={handleSubmit(onCreateForm)}>
              <div className="mb-3">
                <label className="form-label">
                  Version Number <span className="text-danger">*</span>
                </label>

                <Controller
                  name="version_number"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />

                {errors.version_number && (
                  <div className="text-danger small">
                    {errors.version_number.message}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                  <label className="form-label">
                    Apk File <span className="text-danger">*</span>
                  </label>
                </div>

                <Dragger
                  maxCount={1}
                  beforeUpload={(file) => {
                    if (
                      file.type !== "application/vnd.android.package-archive"
                    ) {
                      message.error("Only .apk files are allowed");
                      return Upload.LIST_IGNORE;
                    }
                    setSelectedFile(file);
                    return false;
                  }}
                  onRemove={() => {
                    setSelectedFile(null);
                    setS3Key("");
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <CloudUploadOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area
                  </p>
                  <p className="ant-upload-hint">Only .apk file is allowed</p>
                </Dragger>
              </div>
              <Button
                type="primary"
                loading={fileUploading}
                disabled={!selectedFile || s3Key}
                style={{
                  marginRight: "8px",
                  marginBottom: "8px",
                  width: "100%",
                }}
                onClick={handleUploadToS3}
              >
                Upload to S3
              </Button>

              {s3Key && (
                <div style={{ marginTop: "8px", color: "green" }}>
                  Uploaded to S3
                </div>
              )}
              <Divider plain>Add Apk</Divider>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={!s3Key || fileUploading}
                style={{
                  marginTop: "8px",
                  width: "100%",
                }}
              >
                Add Apk
              </Button>
            </form>
          </Modal>
        )}
      </div>
    </RequirePermission>
  );
};

export default MeetingRoomApk;
